import React, { useState, useEffect, useContext, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, Users, Settings, Plus, Trash2, Play,
  GitBranch, Shield, ChevronUp, Crown, UserX,
  Gift, Star, DollarSign, Image, Eye, Upload, X as XIcon, Link as LinkIcon,
  Edit2, Key, Copy, Check as CheckIcon, AlertTriangle, Lock, Unlock,
  Ban, ShieldOff, Clock, Calendar, Search, ChevronRight, Swords,
  MessageSquare, Globe, Gamepad2, Info
} from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { adminAPI } from '../api';
import { Link } from 'react-router-dom';
import './AdminPage.css';

/* ─────────────────────────────────────────────
   Composant ImageUpload
───────────────────────────────────────────── */
function ImageUpload({ value, onChange, folder = 'logos', label = 'Image', hint = null }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const res = await adminAPI.uploadTournamentImage(file, folder);
      onChange(res.data.url);
    } catch (e) {
      setError(e.message || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="img-upload-wrapper">
      {!manualMode ? (
        <>
          <div
            className={`img-upload-zone${uploading ? ' uploading' : ''}`}
            onClick={() => !uploading && inputRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
          >
            {uploading ? (
              <div className="img-upload-spinner" />
            ) : value ? (
              <div className="img-upload-preview">
                <img src={value} alt="preview" />
                <div className="img-upload-overlay">
                  <Upload size={18} /> Changer
                </div>
              </div>
            ) : (
              <div className="img-upload-placeholder">
                <Upload size={22} />
                <span>Cliquer ou déposer une image</span>
                <small>JPG, PNG, WebP — max 5 Mo</small>
                {hint && <small className="img-upload-hint">{hint}</small>}
              </div>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleChange}
          />
          {value && !uploading && (
            <button
              type="button"
              className="img-upload-remove"
              onClick={() => onChange('')}
              title="Supprimer l'image"
            >
              <XIcon size={13} /> Retirer
            </button>
          )}
          {error && <div className="img-upload-error">{error}</div>}
          <button
            type="button"
            className="img-upload-manual-toggle"
            onClick={() => setManualMode(true)}
          >
            <LinkIcon size={12} /> Entrer une URL
          </button>
        </>
      ) : (
        <>
          <input
            type="url"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="https://example.com/logo.png"
            className="img-upload-url-input"
          />
          <button
            type="button"
            className="img-upload-manual-toggle"
            onClick={() => setManualMode(false)}
          >
            <Upload size={12} /> Upload un fichier
          </button>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   EditTournamentModal
───────────────────────────────────────────── */
function EditTournamentModal({ tournament, onClose, onSaved }) {
  const [formData, setFormData] = useState({
    name: tournament.name || '',
    description: tournament.description || '',
    region: tournament.region || 'EUW',
    format: tournament.format || 'single-elimination',
    maxTeams: tournament.maxTeams || 8,
    startDate: tournament.startDate ? tournament.startDate.substring(0, 10) : '',
    registrationDeadline: tournament.registrationDeadline ? tournament.registrationDeadline.substring(0, 10) : '',
    logo: tournament.logo || '',
    entryFeeAmount: tournament.entryFee?.amount || 0,
    entryFeeCurrency: tournament.entryFee?.currency || 'EUR',
    rewards: (tournament.rewards || []).map(r => ({ place: r.place, description: r.description || '', amount: r.amount || '' })),
    sponsors: (tournament.sponsors || []).map(s => ({ name: s.name || '', logoUrl: s.logoUrl || '', website: s.website || '' })),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Gestion équipes ──
  const [enrolledTeams, setEnrolledTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [teamSearch, setTeamSearch] = useState('');
  const [teamSearchResults, setTeamSearchResults] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);

  // Chargement initial des équipes inscrites
  useEffect(() => {
    adminAPI.getTournamentTeams(tournament._id)
      .then(res => setEnrolledTeams(res.data.teams || []))
      .catch(() => {});
  }, [tournament._id]);

  // Recherche d'équipes à ajouter
  useEffect(() => {
    if (teamSearch.length < 1) { setTeamSearchResults([]); setShowTeamDropdown(false); return; }
    const t = setTimeout(async () => {
      try {
        const res = await adminAPI.getAllTeams(teamSearch);
        const enrolled = enrolledTeams.map(e => e.id);
        setTeamSearchResults((res.data.teams || []).filter(t => !enrolled.includes(t.id)));
        setShowTeamDropdown(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [teamSearch, enrolledTeams]);

  const handleAddTeam = async (team) => {
    setTeamError('');
    setTeamLoading(true);
    try {
      await adminAPI.adminAddTeam(tournament._id, team.id);
      setEnrolledTeams(prev => [...prev, team]);
      setTeamSearch('');
      setTeamSearchResults([]);
      setShowTeamDropdown(false);
    } catch (e) {
      setTeamError(e.message || 'Erreur');
    } finally {
      setTeamLoading(false);
    }
  };

  const handleRemoveTeam = async (teamId) => {
    setTeamError('');
    try {
      await adminAPI.adminRemoveTeam(tournament._id, teamId);
      setEnrolledTeams(prev => prev.filter(t => t.id !== teamId));
    } catch (e) {
      setTeamError(e.message || 'Erreur');
    }
  };

  // Scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const addReward = () => setFormData(f => ({ ...f, rewards: [...f.rewards, { place: f.rewards.length + 1, description: '', amount: '' }] }));
  const updateReward = (i, field, val) => setFormData(f => { const r = [...f.rewards]; r[i] = { ...r[i], [field]: val }; return { ...f, rewards: r }; });
  const removeReward = (i) => setFormData(f => ({ ...f, rewards: f.rewards.filter((_, idx) => idx !== i) }));

  const addSponsor = () => setFormData(f => ({ ...f, sponsors: [...f.sponsors, { name: '', logoUrl: '', website: '' }] }));
  const updateSponsor = (i, field, val) => setFormData(f => { const s = [...f.sponsors]; s[i] = { ...s[i], [field]: val }; return { ...f, sponsors: s }; });
  const removeSponsor = (i) => setFormData(f => ({ ...f, sponsors: f.sponsors.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await adminAPI.updateTournament(tournament._id, {
        name: formData.name,
        description: formData.description,
        region: formData.region,
        format: formData.format,
        maxTeams: parseInt(formData.maxTeams, 10),
        startDate: formData.startDate || undefined,
        registrationDeadline: formData.registrationDeadline || undefined,
        logo: formData.logo || undefined,
        entryFeeAmount: parseFloat(formData.entryFeeAmount) || 0,
        entryFeeCurrency: formData.entryFeeCurrency,
        rewards: formData.rewards.filter(r => r.place && (r.description || r.amount)),
        sponsors: formData.sponsors.filter(s => s.name),
      });
      onSaved();
    } catch (e) {
      setError(e.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="etm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div
        className="etm-panel"
        initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
      >
        <div className="etm-header">
          <h2><Edit2 size={18} /> Modifier le tournoi</h2>
          <button className="etm-close" onClick={onClose}><XIcon size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="etm-body admin-form">
          <div className="admin-form-grid">
            <div className="admin-form-field">
              <label>Nom *</label>
              <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="admin-form-field">
              <label>Région</label>
              <select value={formData.region} onChange={e => setFormData({ ...formData, region: e.target.value })}>
                <option value="EUW">EUW</option>
                <option value="EUNE">EUNE</option>
                <option value="NA">NA</option>
                <option value="KR">KR</option>
              </select>
            </div>
            <div className="admin-form-field">
              <label>Format</label>
              <select value={formData.format} onChange={e => setFormData({ ...formData, format: e.target.value })}>
                <option value="single-elimination">Élimination simple</option>
                <option value="double-elimination">Élimination double</option>
              </select>
            </div>
            <div className="admin-form-field">
              <label>Équipes max</label>
              <select value={formData.maxTeams} onChange={e => setFormData({ ...formData, maxTeams: e.target.value })}>
                {[4, 8, 16, 32].map(n => <option key={n} value={n}>{n} équipes</option>)}
              </select>
            </div>
            <div className="admin-form-field admin-form-field--full">
              <label>Description</label>
              <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>
            <div className="admin-form-field">
              <label>Date de début</label>
              <input type="date" value={formData.startDate} onChange={e => setFormData({ ...formData, startDate: e.target.value })} />
            </div>
            <div className="admin-form-field">
              <label>Clôture des inscriptions</label>
              <input type="date" value={formData.registrationDeadline} onChange={e => setFormData({ ...formData, registrationDeadline: e.target.value })} />
            </div>
            <div className="admin-form-field admin-form-field--full">
              <label><Image size={13} /> Logo du tournoi</label>
              <ImageUpload value={formData.logo} onChange={url => setFormData({ ...formData, logo: url })} folder="logos" />
            </div>
            <div className="admin-form-field">
              <label><DollarSign size={13} /> Frais d'inscription</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input type="number" min="0" step="0.01" value={formData.entryFeeAmount} onChange={e => setFormData({ ...formData, entryFeeAmount: e.target.value })} style={{ flex: 2 }} />
                <select value={formData.entryFeeCurrency} onChange={e => setFormData({ ...formData, entryFeeCurrency: e.target.value })} style={{ flex: 1 }}>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
          </div>

          {/* Équipes inscrites */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <span><Users size={14} /> Équipes ({enrolledTeams.length}/{formData.maxTeams})</span>
            </div>

            {/* Barre de recherche */}
            <div className="etm-team-search-wrap">
              <div className="etm-team-search-input-row">
                <input
                  type="text"
                  className="etm-team-search-input"
                  placeholder="Rechercher une équipe à ajouter…"
                  value={teamSearch}
                  onChange={e => setTeamSearch(e.target.value)}
                  onFocus={() => teamSearchResults.length > 0 && setShowTeamDropdown(true)}
                  onBlur={() => setTimeout(() => setShowTeamDropdown(false), 150)}
                  disabled={teamLoading}
                />
                {teamSearch && (
                  <button type="button" className="etm-team-search-clear" onClick={() => { setTeamSearch(''); setTeamSearchResults([]); setShowTeamDropdown(false); }}>
                    <XIcon size={13} />
                  </button>
                )}
              </div>
              {showTeamDropdown && teamSearchResults.length > 0 && (
                <ul className="etm-team-dropdown">
                  {teamSearchResults.map(t => (
                    <li key={t.id} className="etm-team-dropdown-item" onMouseDown={() => handleAddTeam(t)}>
                      <span className="etm-team-tag">{t.tag || '??'}</span>
                      <span className="etm-team-name">{t.name}</span>
                      {t.captainName && <span className="etm-team-captain">{t.captainName}</span>}
                      <Plus size={13} className="etm-team-add-icon" />
                    </li>
                  ))}
                </ul>
              )}
              {showTeamDropdown && teamSearchResults.length === 0 && teamSearch.length > 0 && (
                <div className="etm-team-dropdown etm-team-dropdown--empty">Aucune équipe trouvée</div>
              )}
            </div>

            {teamError && <div className="admin-error" style={{ marginTop: '0.4rem' }}>{teamError}</div>}

            {/* Liste des équipes inscrites */}
            {enrolledTeams.length === 0 ? (
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>Aucune équipe inscrite</p>
            ) : (
              <ul className="etm-enrolled-list">
                {enrolledTeams.map((t, i) => (
                  <li key={t.id} className="etm-enrolled-item">
                    <span className="etm-enrolled-seed">#{i + 1}</span>
                    <span className="etm-team-tag">{t.tag || '??'}</span>
                    <span className="etm-enrolled-name">{t.name}</span>
                    {t.captainName && <span className="etm-team-captain">{t.captainName}</span>}
                    <button type="button" className="admin-action-btn danger small etm-enrolled-remove" onClick={() => handleRemoveTeam(t.id)} title="Retirer l'équipe">
                      <Trash2 size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Récompenses */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <span><Gift size={14} /> Récompenses</span>
              <button type="button" className="admin-action-btn ghost small" onClick={addReward}><Plus size={13} /> Ajouter</button>
            </div>
            {formData.rewards.map((r, i) => (
              <div key={i} className="admin-list-row">
                <input type="number" min="1" value={r.place} onChange={e => updateReward(i, 'place', parseInt(e.target.value))} placeholder="Place" style={{ width: '70px' }} />
                <input type="text" value={r.description} onChange={e => updateReward(i, 'description', e.target.value)} placeholder="Description" style={{ flex: 2 }} />
                <input type="text" value={r.amount} onChange={e => updateReward(i, 'amount', e.target.value)} placeholder="Montant" style={{ flex: 1 }} />
                <button type="button" className="admin-action-btn danger small" onClick={() => removeReward(i)}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>

          {/* Sponsors */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <span><Star size={14} /> Sponsors</span>
              <button type="button" className="admin-action-btn ghost small" onClick={addSponsor}><Plus size={13} /> Ajouter</button>
            </div>
            {formData.sponsors.map((s, i) => (
              <div key={i} className="admin-sponsor-row">
                <div className="admin-sponsor-fields">
                  <input type="text" value={s.name} onChange={e => updateSponsor(i, 'name', e.target.value)} placeholder="Nom du sponsor" className="admin-sponsor-name" />
                  <input type="url" value={s.website} onChange={e => updateSponsor(i, 'website', e.target.value)} placeholder="Site web" className="admin-sponsor-website" />
                </div>
                <div className="admin-sponsor-logo-row">
                  <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'block' }}>Logo</label>
                  <ImageUpload value={s.logoUrl} onChange={url => updateSponsor(i, 'logoUrl', url)} folder="sponsors" hint="PNG transparent — 400×160 px (ratio 5:2)" />
                </div>
                <button type="button" className="admin-action-btn danger small admin-sponsor-remove" onClick={() => removeSponsor(i)}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>

          {error && <div className="admin-error">{error}</div>}
          <div className="admin-form-actions">
            <button type="submit" className="admin-action-btn primary" disabled={saving}>
              {saving ? 'Sauvegarde…' : <><CheckIcon size={15} /> Sauvegarder</>}
            </button>
            <button type="button" className="admin-action-btn ghost" onClick={onClose}>Annuler</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CodesModal
───────────────────────────────────────────── */
function CodesModal({ tournament, onClose }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [simulated, setSimulated] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => { loadMatches(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMatches = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getTournament(tournament._id);
      setMatches(res.data.matches || []);
    } catch (e) {
      setError('Impossible de charger les matches');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await adminAPI.generateTournamentCodes(tournament._id);
      setMatches(res.data.matches || []);
      setSimulated(res.data.simulated);
    } catch (e) {
      setError(e.message || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(code);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  return (
    <div className="etm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div
        className="etm-panel etm-panel--codes"
        initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
      >
        <div className="etm-header">
          <h2><Key size={18} /> Codes tournoi — {tournament.name}</h2>
          <button className="etm-close" onClick={onClose}><XIcon size={18} /></button>
        </div>

        <div className="etm-body">
          <p className="codes-info">
            Les codes permettent aux joueurs de rejoindre le lobby depuis le client LoL (Jouer → Tournoi → Entrer un code).
            <br /><strong>1 code = 1 match</strong>.
          </p>

          {simulated && (
            <div className="codes-warning">
              <AlertTriangle size={14} /> Codes <strong>simulés</strong> (TEST-…) — nécessite une clé Tournament API de prod pour de vrais codes Riot.
            </div>
          )}

          {matches.length === 0 && !loading && (
            <div className="admin-empty" style={{ padding: '2rem 0' }}>
              <GitBranch size={36} />
              <p>Aucun match — générez d'abord le bracket.</p>
            </div>
          )}

          {loading ? (
            <div className="admin-loading">Chargement…</div>
          ) : (
            <div className="codes-list">
              {matches.map((m, i) => {
                const code = m.tournament_code || m.tournamentCode;
                return (
                  <div key={m.id || i} className="codes-row">
                    <div className="codes-match-info">
                      <span className="codes-round">{m.round || `R${i + 1}`}</span>
                      <span className="codes-match-num">Match {m.match_number || m.matchNumber || (i + 1)}</span>
                    </div>
                    {code ? (
                      <div className="codes-code-cell">
                        <code className="codes-code">{code}</code>
                        <button
                          className={`codes-copy-btn${copied === code ? ' copied' : ''}`}
                          onClick={() => copyCode(code)}
                          title="Copier"
                        >
                          {copied === code ? <CheckIcon size={13} /> : <Copy size={13} />}
                        </button>
                      </div>
                    ) : (
                      <span className="codes-no-code">— pas de code</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {error && <div className="admin-error">{error}</div>}

          <div className="etm-footer">
            <button
              className="admin-action-btn primary"
              onClick={handleGenerate}
              disabled={generating || matches.length === 0}
            >
              {generating ? 'Génération…' : <><Key size={15} /> Générer les codes</>}
            </button>
            <button className="admin-action-btn ghost" onClick={onClose}>Fermer</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function statusLabel(status) {
  const map = {
    registration: 'Inscriptions',
    seeding: 'Seeding',
    'in-progress': 'En cours',
    finished: 'Terminé',
  };
  return map[status] || status;
}

function statusClass(status) {
  const map = {
    registration: 'status-registration',
    seeding: 'status-seeding',
    'in-progress': 'status-inprogress',
    finished: 'status-finished',
  };
  return map[status] || '';
}

/* ─────────────────────────────────────────────
   AdminPage principal
───────────────────────────────────────────── */
export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('tournaments');

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="admin-page"
    >
      <div className="admin-header">
        <div className="admin-header-icon">
          <Settings size={28} />
        </div>
        <div>
          <h1>Panel Administrateur</h1>
          <p>Gestion des tournois et des administrateurs</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab${activeTab === 'tournaments' ? ' active' : ''}`}
          onClick={() => setActiveTab('tournaments')}
        >
          <Trophy size={18} /> Tournois
        </button>
        <button
          className={`admin-tab${activeTab === 'admins' ? ' active' : ''}`}
          onClick={() => setActiveTab('admins')}
        >
          <Users size={18} /> Administrateurs
        </button>
      </div>

      <div className="admin-body">
        <AnimatePresence mode="wait">
          {activeTab === 'tournaments' ? (
            <motion.div
              key="tournaments"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.18 }}
            >
              <TournamentsTab />
            </motion.div>
          ) : (
            <motion.div
              key="admins"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.18 }}
            >
              <AdminsTab />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   Onglet Tournois
───────────────────────────────────────────── */
function TournamentsTab() {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [codesTournament, setCodesTournament] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRegion, setFilterRegion] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    region: 'EUW',
    format: 'single-elimination',
    maxTeams: 8,
    startDate: '',
    registrationDeadline: '',
    logo: '',
    entryFeeAmount: 0,
    entryFeeCurrency: 'EUR',
    rewards: [],
    sponsors: [],
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadTournaments(); }, []);

  const loadTournaments = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getTournaments();
      setTournaments(res.data);
    } catch (e) {
      setError('Erreur lors du chargement des tournois');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      await adminAPI.createTournament({
        name: formData.name,
        description: formData.description,
        region: formData.region,
        format: formData.format,
        maxTeams: parseInt(formData.maxTeams, 10),
        startDate: formData.startDate || undefined,
        registrationDeadline: formData.registrationDeadline || undefined,
        logo: formData.logo || undefined,
        entryFee: { amount: parseFloat(formData.entryFeeAmount) || 0, currency: formData.entryFeeCurrency },
        rewards: formData.rewards.filter(r => r.place && (r.description || r.amount)),
        sponsors: formData.sponsors.filter(s => s.name),
      });
      setFormData({ name: '', description: '', region: 'EUW', format: 'single-elimination', maxTeams: 8, startDate: '', registrationDeadline: '', logo: '', entryFeeAmount: 0, entryFeeCurrency: 'EUR', rewards: [], sponsors: [] });
      setShowForm(false);
      loadTournaments();
    } catch (e) {
      setError(e.message || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  // Helpers listes dynamiques
  const addReward = () => setFormData(f => ({ ...f, rewards: [...f.rewards, { place: f.rewards.length + 1, description: '', amount: '' }] }));
  const updateReward = (i, field, val) => setFormData(f => { const r = [...f.rewards]; r[i] = { ...r[i], [field]: val }; return { ...f, rewards: r }; });
  const removeReward = (i) => setFormData(f => ({ ...f, rewards: f.rewards.filter((_, idx) => idx !== i) }));

  const addSponsor = () => setFormData(f => ({ ...f, sponsors: [...f.sponsors, { name: '', logoUrl: '', website: '' }] }));
  const updateSponsor = (i, field, val) => setFormData(f => { const s = [...f.sponsors]; s[i] = { ...s[i], [field]: val }; return { ...f, sponsors: s }; });
  const removeSponsor = (i) => setFormData(f => ({ ...f, sponsors: f.sponsors.filter((_, idx) => idx !== i) }));

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer le tournoi "${name}" ? Cette action est irréversible.`)) return;
    try {
      await adminAPI.deleteTournament(id);
      loadTournaments();
    } catch (e) {
      alert('Erreur: ' + e.message);
    }
  };

  const handleGenerateBracket = async (id) => {
    try {
      await adminAPI.generateBracket(id);
      loadTournaments();
    } catch (e) {
      alert('Erreur: ' + e.message);
    }
  };

  const handleStart = async (id) => {
    try {
      await adminAPI.startTournament(id);
      loadTournaments();
    } catch (e) {
      alert('Erreur: ' + e.message);
    }
  };

  const handleToggleRegistration = async (tournament) => {
    const isOpen = tournament.status === 'registration';
    const action = isOpen ? 'fermer' : 'rouvrir';
    if (!window.confirm(`${isOpen ? 'Fermer' : 'Rouvrir'} les inscriptions pour "${tournament.name}" ?`)) return;
    try {
      await adminAPI.updateTournament(tournament._id, {
        status: isOpen ? 'seeding' : 'registration'
      });
      loadTournaments();
    } catch (e) {
      alert(`Erreur lors de la tentative de ${action} les inscriptions: ` + e.message);
    }
  };

  const filteredTournaments = tournaments.filter(t => {
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterRegion !== 'all' && t.region?.toUpperCase() !== filterRegion) return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const regions = ['all', ...new Set(tournaments.map(t => t.region?.toUpperCase()).filter(Boolean))];

  return (
    <div>
      {/* Toolbar */}
      <div className="admin-toolbar">
        <span className="admin-count">{filteredTournaments.length} / {tournaments.length} tournoi(s)</span>
        <button
          className="admin-action-btn primary"
          onClick={() => setShowForm(v => !v)}
        >
          {showForm ? <ChevronUp size={18} /> : <Plus size={18} />}
          {showForm ? 'Fermer' : 'Créer un tournoi'}
        </button>
      </div>

      {/* Filtres */}
      <div className="admin-filters">
        <input
          type="text"
          className="admin-filter-search"
          placeholder="Rechercher un tournoi…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="admin-filter-pills">
          {[
            { value: 'all',          label: 'Tous' },
            { value: 'registration', label: 'Inscriptions' },
            { value: 'in-progress',  label: 'En cours' },
            { value: 'seeding',      label: 'Seeding' },
            { value: 'finished',     label: 'Terminés' },
          ].map(opt => (
            <button
              key={opt.value}
              className={`admin-filter-pill${filterStatus === opt.value ? ' active' : ''}`}
              onClick={() => setFilterStatus(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {regions.length > 2 && (
          <select
            className="admin-filter-select"
            value={filterRegion}
            onChange={e => setFilterRegion(e.target.value)}
          >
            {regions.map(r => (
              <option key={r} value={r}>{r === 'all' ? 'Toutes régions' : r}</option>
            ))}
          </select>
        )}
      </div>

      {/* Formulaire création */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="admin-form-panel"
          >
            <form onSubmit={handleCreate} className="admin-form">
              <h3><Plus size={16} /> Nouveau tournoi</h3>
              <div className="admin-form-grid">
                <div className="admin-form-field">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Tournoi EUW Summer 2025"
                    required
                  />
                </div>
                <div className="admin-form-field">
                  <label>Région</label>
                  <select
                    value={formData.region}
                    onChange={e => setFormData({ ...formData, region: e.target.value })}
                  >
                    <option value="EUW">EUW</option>
                    <option value="EUNE">EUNE</option>
                    <option value="NA">NA</option>
                    <option value="KR">KR</option>
                  </select>
                </div>
                <div className="admin-form-field">
                  <label>Format</label>
                  <select
                    value={formData.format}
                    onChange={e => setFormData({ ...formData, format: e.target.value })}
                  >
                    <option value="single-elimination">Élimination simple</option>
                    <option value="double-elimination">Élimination double</option>
                  </select>
                </div>
                <div className="admin-form-field">
                  <label>Équipes max</label>
                  <select
                    value={formData.maxTeams}
                    onChange={e => setFormData({ ...formData, maxTeams: e.target.value })}
                  >
                    {[4, 8, 16, 32].map(n => (
                      <option key={n} value={n}>{n} équipes</option>
                    ))}
                  </select>
                </div>
                <div className="admin-form-field admin-form-field--full">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description du tournoi (optionnel)"
                    rows={2}
                  />
                </div>
                <div className="admin-form-field">
                  <label>Date de début</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                  />
                </div>
                <div className="admin-form-field">
                  <label>Clôture des inscriptions</label>
                  <input
                    type="date"
                    value={formData.registrationDeadline}
                    onChange={e => setFormData({ ...formData, registrationDeadline: e.target.value })}
                  />
                </div>
                <div className="admin-form-field admin-form-field--full">
                  <label><Image size={13} /> Logo du tournoi</label>
                  <ImageUpload
                    value={formData.logo}
                    onChange={url => setFormData({ ...formData, logo: url })}
                    folder="logos"
                    label="Logo du tournoi"
                  />
                </div>
                <div className="admin-form-field">
                  <label><DollarSign size={13} /> Frais d'inscription</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.entryFeeAmount}
                      onChange={e => setFormData({ ...formData, entryFeeAmount: e.target.value })}
                      placeholder="0"
                      style={{ flex: 2 }}
                    />
                    <select
                      value={formData.entryFeeCurrency}
                      onChange={e => setFormData({ ...formData, entryFeeCurrency: e.target.value })}
                      style={{ flex: 1 }}
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Récompenses */}
              <div className="admin-form-section">
                <div className="admin-form-section-header">
                  <span><Gift size={14} /> Récompenses</span>
                  <button type="button" className="admin-action-btn ghost small" onClick={addReward}>
                    <Plus size={13} /> Ajouter
                  </button>
                </div>
                {formData.rewards.map((r, i) => (
                  <div key={i} className="admin-list-row">
                    <input type="number" min="1" value={r.place} onChange={e => updateReward(i, 'place', parseInt(e.target.value))} placeholder="Place" style={{ width: '70px' }} />
                    <input type="text" value={r.description} onChange={e => updateReward(i, 'description', e.target.value)} placeholder="Description (ex: Trophée)" style={{ flex: 2 }} />
                    <input type="text" value={r.amount} onChange={e => updateReward(i, 'amount', e.target.value)} placeholder="Montant (ex: 500€)" style={{ flex: 1 }} />
                    <button type="button" className="admin-action-btn danger small" onClick={() => removeReward(i)}><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>

              {/* Sponsors */}
              <div className="admin-form-section">
                <div className="admin-form-section-header">
                  <span><Star size={14} /> Sponsors</span>
                  <button type="button" className="admin-action-btn ghost small" onClick={addSponsor}>
                    <Plus size={13} /> Ajouter
                  </button>
                </div>
                {formData.sponsors.map((s, i) => (
                  <div key={i} className="admin-sponsor-row">
                    <div className="admin-sponsor-fields">
                      <input type="text" value={s.name} onChange={e => updateSponsor(i, 'name', e.target.value)} placeholder="Nom du sponsor" className="admin-sponsor-name" />
                      <input type="url" value={s.website} onChange={e => updateSponsor(i, 'website', e.target.value)} placeholder="Site web (optionnel)" className="admin-sponsor-website" />
                    </div>
                    <div className="admin-sponsor-logo-row">
                      <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'block' }}>Logo</label>
                      <ImageUpload
                        value={s.logoUrl}
                        onChange={url => updateSponsor(i, 'logoUrl', url)}
                        folder="sponsors"
                        label="Logo sponsor"
                        hint="PNG transparent — 400×160 px (ratio 5:2)"
                      />
                    </div>
                    <button type="button" className="admin-action-btn danger small admin-sponsor-remove" onClick={() => removeSponsor(i)}><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>

              {error && <div className="admin-error">{error}</div>}
              <div className="admin-form-actions">
                <button type="submit" className="admin-action-btn primary" disabled={creating}>
                  {creating ? 'Création…' : <><Plus size={16} /> Créer</>}
                </button>
                <button type="button" className="admin-action-btn ghost" onClick={() => setShowForm(false)}>
                  Annuler
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Liste tournois */}
      {loading ? (
        <div className="admin-loading">Chargement…</div>
      ) : filteredTournaments.length === 0 ? (
        <div className="admin-empty">
          <Trophy size={48} />
          <p>{tournaments.length === 0 ? 'Aucun tournoi créé' : 'Aucun tournoi ne correspond aux filtres'}</p>
        </div>
      ) : (
        <div className="tournament-admin-list">
          {filteredTournaments.map((t, i) => (
            <motion.div
              key={t._id}
              className="tournament-admin-card"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <div className="tac-left">
                <div className="tac-name">
                  <Trophy size={18} />
                  {t.name}
                </div>
                <div className="tac-meta">
                  <span className={`status-badge ${statusClass(t.status)}`}>
                    {statusLabel(t.status)}
                  </span>
                  <span className="tac-region">{t.region}</span>
                  <span className="tac-teams">
                    {t.teams?.length || 0} / {t.maxTeams} équipes
                  </span>
                  <span className="tac-format">{t.format}</span>
                </div>
              </div>
              <div className="tac-actions">
                <Link
                  to={`/tournament/${t._id}`}
                  className="admin-action-btn ghost"
                  title="Voir la page"
                >
                  <Eye size={16} />
                </Link>
                <button
                  className="admin-action-btn ghost"
                  onClick={() => setEditingTournament(t)}
                  title="Modifier le tournoi"
                >
                  <Edit2 size={16} />
                </button>
                {(t.status === 'registration' || t.status === 'seeding') && (
                  <button
                    className={`admin-action-btn ${t.status === 'registration' ? 'warning' : 'reopen'}`}
                    onClick={() => handleToggleRegistration(t)}
                    title={t.status === 'registration' ? 'Fermer les inscriptions' : 'Rouvrir les inscriptions'}
                  >
                    {t.status === 'registration'
                      ? <><Lock size={16} /> Fermer inscriptions</>
                      : <><Unlock size={16} /> Rouvrir inscriptions</>
                    }
                  </button>
                )}
                {t.status === 'registration' && (
                  <button
                    className="admin-action-btn secondary"
                    onClick={() => handleGenerateBracket(t._id)}
                    title="Générer le bracket"
                  >
                    <GitBranch size={16} /> Bracket
                  </button>
                )}
                {t.status === 'seeding' && (
                  <button
                    className="admin-action-btn success"
                    onClick={() => handleStart(t._id)}
                    title="Démarrer le tournoi"
                  >
                    <Play size={16} /> Démarrer
                  </button>
                )}
                {(t.status === 'seeding' || t.status === 'in-progress') && (
                  <button
                    className="admin-action-btn codes-btn"
                    onClick={() => setCodesTournament(t)}
                    title="Codes tournoi Riot"
                  >
                    <Key size={16} /> Codes
                  </button>
                )}
                <button
                  className="admin-action-btn danger"
                  onClick={() => handleDelete(t._id, t.name)}
                  title="Supprimer"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modales */}
      <AnimatePresence>
        {editingTournament && (
          <EditTournamentModal
            key="edit-modal"
            tournament={editingTournament}
            onClose={() => setEditingTournament(null)}
            onSaved={() => { setEditingTournament(null); loadTournaments(); }}
          />
        )}
        {codesTournament && (
          <CodesModal
            key="codes-modal"
            tournament={codesTournament}
            onClose={() => setCodesTournament(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Onglet Utilisateurs (ex-Administrateurs)
───────────────────────────────────────────── */
function AdminsTab() {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [banModal, setBanModal] = useState(null);
  const [banForm, setBanForm] = useState({ reason: '', duration: '', syncDiscord: true });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminAPI.getUsers();
      const sorted = [...res.data.users].sort((a, b) => {
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (b.role === 'admin' && a.role !== 'admin') return 1;
        return 0;
      });
      setUsers(sorted);
    } catch (e) {
      setError('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const openUserDetail = async (userId) => {
    setSelectedUser(userId);
    setDetailLoading(true);
    try {
      const res = await adminAPI.getUserDetail(userId);
      setUserDetail(res.data);
    } catch (e) {
      setUserDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setSelectedUser(null); setUserDetail(null); };

  const handlePromote = async (userId) => {
    setActionLoading(true);
    try { await adminAPI.makeAdmin(userId); loadUsers(); if (userDetail?.id === userId) openUserDetail(userId); }
    catch (e) { alert('Erreur: ' + e.message); }
    finally { setActionLoading(false); }
  };

  const handleDemote = async (userId, name) => {
    if (!window.confirm(`Rétrograder "${name}" ? Il perdra l'accès admin.`)) return;
    setActionLoading(true);
    try { await adminAPI.removeAdmin(userId); loadUsers(); if (userDetail?.id === userId) openUserDetail(userId); }
    catch (e) { alert('Erreur: ' + e.message); }
    finally { setActionLoading(false); }
  };

  const openBanModal = (user) => {
    setBanModal(user);
    setBanForm({ reason: '', duration: '', syncDiscord: true });
  };

  const handleBan = async () => {
    if (!banModal) return;
    setActionLoading(true);
    try {
      const duration = banForm.duration ? parseInt(banForm.duration, 10) : null;
      await adminAPI.banUser(banModal.id, {
        reason: banForm.reason || 'Aucune raison spécifiée',
        duration,
        syncDiscord: banForm.syncDiscord,
      });
      setBanModal(null);
      loadUsers();
      if (userDetail?.id === banModal.id) openUserDetail(banModal.id);
    } catch (e) {
      alert('Erreur: ' + e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnban = async (userId) => {
    if (!window.confirm('Débannir cet utilisateur ?')) return;
    setActionLoading(true);
    try {
      await adminAPI.unbanUser(userId, { syncDiscord: true });
      loadUsers();
      if (userDetail?.id === userId) openUserDetail(userId);
    } catch (e) {
      alert('Erreur: ' + e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const getInitials = (u) => {
    const name = u.display_name || u.riot_id || u.username || '?';
    return name.substring(0, 2).toUpperCase();
  };

  const getName = (u) => u.display_name || u.riot_id || u.username || '—';

  const isBanExpired = (u) => u.banned && u.ban_until && new Date(u.ban_until) < new Date();
  const isBanActive = (u) => u.banned && !isBanExpired(u);

  const filteredUsers = users.filter(u => {
    const name = getName(u).toLowerCase();
    const riot = (u.riot_id || '').toLowerCase();
    const email = (u.email || '').toLowerCase();
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || name.includes(q) || riot.includes(q) || email.includes(q);
    const matchRole =
      filterRole === 'all' ||
      (filterRole === 'admin' && u.role === 'admin') ||
      (filterRole === 'banned' && isBanActive(u)) ||
      (filterRole === 'user' && u.role === 'user' && !isBanActive(u));
    return matchSearch && matchRole;
  });

  const bannedCount = users.filter(u => isBanActive(u)).length;
  const adminCount = users.filter(u => u.role === 'admin').length;

  const TIER_COLORS = {
    IRON: '#6b5b4f', BRONZE: '#8c6239', SILVER: '#8a9ba8',
    GOLD: '#cd8032', PLATINUM: '#22aaa1', EMERALD: '#10b981',
    DIAMOND: '#5383e8', MASTER: '#9d48e0', GRANDMASTER: '#e84057', CHALLENGER: '#f4c874',
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="aut-toolbar">
        <div className="aut-search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Rechercher par nom, Riot ID, email..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="aut-search-clear" onClick={() => setSearchQuery('')}>
              <XIcon size={13} />
            </button>
          )}
        </div>
        <div className="aut-filters">
          {[
            { key: 'all', label: 'Tous', count: users.length },
            { key: 'admin', label: 'Admins', count: adminCount },
            { key: 'banned', label: 'Bannis', count: bannedCount },
            { key: 'user', label: 'Joueurs', count: users.length - adminCount - bannedCount },
          ].map(f => (
            <button
              key={f.key}
              className={`aut-filter-pill${filterRole === f.key ? ' active' : ''}`}
              onClick={() => setFilterRole(f.key)}
            >
              {f.label} <span className="aut-filter-count">{f.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="aut-results-info">
        {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''}
      </div>

      {loading ? (
        <div className="admin-loading">Chargement…</div>
      ) : error ? (
        <div className="admin-error">{error}</div>
      ) : (
        <div className="admin-users-list">
          {filteredUsers.map((u, i) => {
            const isMe = u.id === currentUser?._id || u.id === currentUser?.userId;
            const name = getName(u);
            const banned = isBanActive(u);
            return (
              <motion.div
                key={u.id}
                className={`admin-user-row${banned ? ' admin-user-row--banned' : ''}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.3) }}
                onClick={() => openUserDetail(u.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className={`aur-avatar${banned ? ' aur-avatar--banned' : ''}`}>
                  {getInitials(u)}
                </div>
                <div className="aur-info">
                  <div className="aur-name">
                    {name}
                    {isMe && <span className="aur-me-badge">Vous</span>}
                    {banned && <span className="aur-ban-badge">Banni</span>}
                  </div>
                  {u.riot_id && (
                    <div className="aur-riot">
                      {u.riot_id}{u.tag_line ? `#${u.tag_line}` : ''}
                    </div>
                  )}
                  {banned && u.ban_until && (
                    <div className="aur-ban-until">
                      <Clock size={11} /> Jusqu'au {new Date(u.ban_until).toLocaleDateString('fr-FR')}
                    </div>
                  )}
                  {banned && !u.ban_until && (
                    <div className="aur-ban-until aur-ban-until--permanent">
                      <Ban size={11} /> Permanent
                    </div>
                  )}
                </div>
                <div className="aur-role">
                  <span className={`role-badge${u.role === 'admin' ? ' admin' : ''}${banned ? ' banned' : ''}`}>
                    {banned ? <><Ban size={12} /> Banni</> : u.role === 'admin' ? <><Crown size={12} /> Admin</> : 'User'}
                  </span>
                </div>
                <div className="aur-actions" onClick={e => e.stopPropagation()}>
                  {!isMe && !banned && u.role !== 'admin' && (
                    <button className="admin-action-btn promote-btn" onClick={() => handlePromote(u.id)} title="Promouvoir admin">
                      <Crown size={14} />
                    </button>
                  )}
                  {!isMe && u.role === 'admin' && (
                    <button className="admin-action-btn demote-btn" onClick={() => handleDemote(u.id, name)} title="Rétrograder">
                      <UserX size={14} />
                    </button>
                  )}
                  {!isMe && !banned && u.role !== 'admin' && (
                    <button className="admin-action-btn ban-btn" onClick={() => openBanModal(u)} title="Bannir">
                      <Ban size={14} />
                    </button>
                  )}
                  {!isMe && banned && (
                    <button className="admin-action-btn unban-btn" onClick={() => handleUnban(u.id)} title="Débannir">
                      <ShieldOff size={14} />
                    </button>
                  )}
                  <ChevronRight size={16} className="aur-chevron" />
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ══════════════ MODALE DÉTAIL UTILISATEUR ══════════════ */}
      <AnimatePresence>
        {selectedUser && (
          <motion.div
            key="user-detail"
            className="etm-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeDetail}
          >
            <motion.div
              className="etm-panel udm-panel"
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="etm-header">
                <h2><Info size={18} /> Détail du joueur</h2>
                <button className="etm-close" onClick={closeDetail}><XIcon size={18} /></button>
              </div>

              <div className="etm-body">
                {detailLoading ? (
                  <div className="admin-loading">Chargement…</div>
                ) : !userDetail ? (
                  <div className="admin-error">Impossible de charger les détails</div>
                ) : (
                  <div className="udm-content">
                    {/* Header profil */}
                    <div className="udm-profile-header">
                      <div className={`udm-avatar${isBanActive(userDetail) ? ' udm-avatar--banned' : ''}`}>
                        {userDetail.profile_icon_id ? (
                          <img
                            src={`https://ddragon.leagueoflegends.com/cdn/14.9.1/img/profileicon/${userDetail.profile_icon_id}.png`}
                            alt="icon"
                          />
                        ) : (
                          <span>{getInitials(userDetail)}</span>
                        )}
                      </div>
                      <div className="udm-profile-info">
                        <h3 className="udm-profile-name">{getName(userDetail)}</h3>
                        {userDetail.riot_id && (
                          <div className="udm-riot-id">
                            <Gamepad2 size={13} /> {userDetail.riot_id}#{userDetail.tag_line}
                          </div>
                        )}
                        {userDetail.email && (
                          <div className="udm-email">{userDetail.email}</div>
                        )}
                        <div className="udm-badges">
                          <span className={`role-badge${userDetail.role === 'admin' ? ' admin' : ''}${isBanActive(userDetail) ? ' banned' : ''}`}>
                            {isBanActive(userDetail) ? <><Ban size={11} /> Banni</> : userDetail.role === 'admin' ? <><Crown size={11} /> Admin</> : 'Joueur'}
                          </span>
                          {userDetail.discord_id && (
                            <span className="udm-badge udm-badge--discord">
                              <MessageSquare size={11} /> Discord lié
                            </span>
                          )}
                          {userDetail.verified && (
                            <span className="udm-badge udm-badge--verified">
                              <CheckIcon size={11} /> Vérifié
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Ban info */}
                    {isBanActive(userDetail) && (
                      <div className="udm-ban-info">
                        <div className="udm-ban-info-header"><Ban size={15} /> Bannissement actif</div>
                        <div className="udm-ban-detail">
                          <span>Raison :</span> {userDetail.ban_reason || '—'}
                        </div>
                        <div className="udm-ban-detail">
                          <span>Durée :</span> {userDetail.ban_until
                            ? `Jusqu'au ${new Date(userDetail.ban_until).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`
                            : 'Permanent'}
                        </div>
                        <div className="udm-ban-detail">
                          <span>Banni le :</span> {userDetail.banned_at ? new Date(userDetail.banned_at).toLocaleDateString('fr-FR') : '—'}
                        </div>
                        {userDetail.banned_by_user && (
                          <div className="udm-ban-detail">
                            <span>Par :</span> {userDetail.banned_by_user.display_name || userDetail.banned_by_user.username}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Infos Riot */}
                    {userDetail.riot_id && (
                      <div className="udm-section">
                        <h4><Gamepad2 size={14} /> Riot Games</h4>
                        <div className="udm-grid">
                          <div className="udm-stat">
                            <span className="udm-stat-label">Région</span>
                            <span className="udm-stat-value">{(userDetail.region || '?').toUpperCase()}</span>
                          </div>
                          <div className="udm-stat">
                            <span className="udm-stat-label">Niveau</span>
                            <span className="udm-stat-value">{userDetail.level || '—'}</span>
                          </div>
                          {userDetail.tier && (
                            <div className="udm-stat">
                              <span className="udm-stat-label">Rang Solo/Duo</span>
                              <span className="udm-stat-value" style={{ color: TIER_COLORS[userDetail.tier] || '#fff' }}>
                                {userDetail.tier} {userDetail.rank}
                              </span>
                            </div>
                          )}
                          {userDetail.flex_tier && (
                            <div className="udm-stat">
                              <span className="udm-stat-label">Rang Flex</span>
                              <span className="udm-stat-value" style={{ color: TIER_COLORS[userDetail.flex_tier] || '#fff' }}>
                                {userDetail.flex_tier} {userDetail.flex_rank}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Équipes */}
                    {userDetail.teams?.length > 0 && (
                      <div className="udm-section">
                        <h4><Users size={14} /> Équipes ({userDetail.teams.length})</h4>
                        <div className="udm-list">
                          {userDetail.teams.map(t => (
                            <div key={t.id} className="udm-list-item">
                              <Shield size={14} />
                              <span>{t.name}</span>
                              {t.tag && <span className="udm-team-tag">[{t.tag}]</span>}
                              {t.owner_id === userDetail.id && <span className="udm-owner-badge">Capitaine</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tournois */}
                    {userDetail.tournaments?.length > 0 && (
                      <div className="udm-section">
                        <h4><Swords size={14} /> Tournois ({userDetail.tournaments.length})</h4>
                        <div className="udm-list">
                          {userDetail.tournaments.map(t => (
                            <div key={t.id} className="udm-list-item">
                              <Trophy size={14} />
                              <span>{t.name}</span>
                              <span className={`udm-status udm-status--${t.status}`}>{t.status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Meta */}
                    <div className="udm-section udm-section--meta">
                      <div className="udm-meta-row">
                        <Calendar size={13} /> Inscrit le {new Date(userDetail.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </div>
                      {userDetail.discord_username && (
                        <div className="udm-meta-row">
                          <MessageSquare size={13} /> Discord : {userDetail.discord_username}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {(userDetail.id !== currentUser?._id && userDetail.id !== currentUser?.userId) && (
                      <div className="udm-actions">
                        {!isBanActive(userDetail) && userDetail.role !== 'admin' && (
                          <>
                            <button className="admin-action-btn promote-btn" onClick={() => handlePromote(userDetail.id)} disabled={actionLoading}>
                              <Crown size={14} /> Promouvoir Admin
                            </button>
                            <button className="admin-action-btn ban-btn" onClick={() => { closeDetail(); openBanModal(userDetail); }} disabled={actionLoading}>
                              <Ban size={14} /> Bannir
                            </button>
                          </>
                        )}
                        {userDetail.role === 'admin' && (
                          <button className="admin-action-btn demote-btn" onClick={() => handleDemote(userDetail.id, getName(userDetail))} disabled={actionLoading}>
                            <UserX size={14} /> Rétrograder
                          </button>
                        )}
                        {isBanActive(userDetail) && (
                          <button className="admin-action-btn unban-btn" onClick={() => handleUnban(userDetail.id)} disabled={actionLoading}>
                            <ShieldOff size={14} /> Débannir
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════ MODALE BAN ══════════════ */}
      <AnimatePresence>
        {banModal && (
          <motion.div
            key="ban-modal"
            className="etm-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setBanModal(null)}
          >
            <motion.div
              className="etm-panel ban-modal-panel"
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="etm-header ban-modal-header">
                <h2><Ban size={18} /> Bannir {getName(banModal)}</h2>
                <button className="etm-close" onClick={() => setBanModal(null)}><XIcon size={18} /></button>
              </div>

              <div className="etm-body">
                <div className="ban-form">
                  <div className="ban-form-group">
                    <label>Raison du bannissement</label>
                    <textarea
                      className="ban-form-textarea"
                      placeholder="Ex: Comportement toxique, triche, non-respect des règles..."
                      value={banForm.reason}
                      onChange={e => setBanForm({ ...banForm, reason: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div className="ban-form-group">
                    <label>Durée</label>
                    <div className="ban-duration-options">
                      {[
                        { value: '', label: 'Permanent' },
                        { value: '1', label: '1 jour' },
                        { value: '3', label: '3 jours' },
                        { value: '7', label: '1 semaine' },
                        { value: '14', label: '2 semaines' },
                        { value: '30', label: '1 mois' },
                        { value: '90', label: '3 mois' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          className={`ban-duration-btn${banForm.duration === opt.value ? ' active' : ''}${opt.value === '' ? ' ban-duration-btn--permanent' : ''}`}
                          onClick={() => setBanForm({ ...banForm, duration: opt.value })}
                          type="button"
                        >
                          {opt.value === '' ? <Ban size={12} /> : <Clock size={12} />}
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="ban-form-group">
                    <label className="ban-checkbox-label">
                      <input
                        type="checkbox"
                        checked={banForm.syncDiscord}
                        onChange={e => setBanForm({ ...banForm, syncDiscord: e.target.checked })}
                      />
                      <MessageSquare size={14} />
                      Synchroniser avec Discord
                      {banModal.discord_id ? (
                        <span className="ban-discord-status ban-discord-status--linked">Lié</span>
                      ) : (
                        <span className="ban-discord-status ban-discord-status--unlinked">Non lié</span>
                      )}
                    </label>
                    {banForm.syncDiscord && banModal.discord_id && (
                      <div className="ban-discord-info">
                        L'utilisateur sera également banni du serveur Discord.
                      </div>
                    )}
                    {banForm.syncDiscord && !banModal.discord_id && (
                      <div className="ban-discord-info ban-discord-info--warn">
                        Aucun compte Discord lié — le ban Discord ne sera pas appliqué.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="etm-footer">
                <button className="admin-action-btn" onClick={() => setBanModal(null)} style={{ flex: 1 }}>
                  Annuler
                </button>
                <button className="admin-action-btn ban-confirm-btn" onClick={handleBan} disabled={actionLoading} style={{ flex: 2 }}>
                  <Ban size={14} /> {actionLoading ? 'Bannissement...' : `Bannir ${banForm.duration ? 'temporairement' : 'définitivement'}`}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
